import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import { DataGridColumnSelectorOrderButton } from './DataGridColumnSelectorOrder';
import DataGridToolbarScrollNudge from './DataGridToolbarScrollNudge';
import {
  clearColumnFieldOrder,
  loadColumnFieldOrder,
  mergeColumnFieldOrder,
  saveColumnFieldOrder,
} from '../../utils/dataGridColumnOrder';

/**
 * Community DataGrid (MIT) — no license watermark.
 * Column drag-resize and header drag-reorder are Pro features; list applets use Column order dialog instead.
 */
function ColumnsOnlyToolbar() {
  return (
    <GridToolbarContainer sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 0.5 }}>
      <DataGridColumnSelectorOrderButton showVisibility orderedColumns={[]} />
    </GridToolbarContainer>
  );
}

/** Height wrapper for General Items-style lists (use with autoHeight={false} on DataGrid). */
export function GeneralItemsGridFrame({ children, sx }) {
  return (
    <Box
      sx={{
        width: '100%',
        height: { xs: 'min(66vh, 540px)', sm: 'min(72vh, 760px)' },
        minHeight: 360,
        maxHeight: 920,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default function ErpDataGrid({
  sx,
  slots,
  showColumnSelector = true,
  listApplet = false,
  columnOrderStorageKey,
  columns,
  ...props
}) {
  const theme = useTheme();
  const [fieldOrder, setFieldOrder] = useState(() =>
    columnOrderStorageKey ? loadColumnFieldOrder(columnOrderStorageKey) : null
  );

  useEffect(() => {
    if (columnOrderStorageKey) {
      setFieldOrder(loadColumnFieldOrder(columnOrderStorageKey));
    } else {
      setFieldOrder(null);
    }
  }, [columnOrderStorageKey]);

  const orderedColumns = useMemo(
    () => mergeColumnFieldOrder(columns || [], fieldOrder),
    [columns, fieldOrder]
  );

  const ListAppletToolbar = useCallback(
    function ListAppletToolbarInner() {
      return (
        <GridToolbarContainer
          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, px: 1, pt: 0.5, flexWrap: 'wrap' }}
        >
          <DataGridToolbarScrollNudge />
          <DataGridColumnSelectorOrderButton
            showVisibility={showColumnSelector}
            columnOrderStorageKey={columnOrderStorageKey}
            orderedColumns={orderedColumns}
            onSaveColumnOrder={
              columnOrderStorageKey
                ? (fields) => {
                    setFieldOrder(fields);
                    saveColumnFieldOrder(columnOrderStorageKey, fields);
                  }
                : undefined
            }
            onResetColumnOrder={
              columnOrderStorageKey
                ? () => {
                    setFieldOrder(null);
                    clearColumnFieldOrder(columnOrderStorageKey);
                  }
                : undefined
            }
          />
        </GridToolbarContainer>
      );
    },
    [showColumnSelector, columnOrderStorageKey, orderedColumns, setFieldOrder]
  );

  const defaultToolbarSlot =
    !slots?.toolbar && listApplet && (showColumnSelector || columnOrderStorageKey)
      ? ListAppletToolbar
      : !slots?.toolbar && showColumnSelector
        ? ColumnsOnlyToolbar
        : null;

  const mergedSlots = {
    ...(slots || {}),
    ...(defaultToolbarSlot ? { toolbar: defaultToolbarSlot } : {}),
  };

  const thumbBase = alpha(theme.palette.text.secondary, 0.48);
  const thumbHover = alpha(theme.palette.text.secondary, 0.68);

  const appletSx = listApplet
    ? {
        height: '100%',
        width: '100%',
        minHeight: 0,
        flex: 1,
        '& .MuiDataGrid-root': {
          backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.55 : 0.72),
          borderRadius: 1,
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
        // Permanent horizontal track (user OK with always-on); brighten thumb when pointer is over the list applet.
        '& .MuiDataGrid-virtualScroller': {
          overflowX: 'scroll',
          overflowY: 'auto',
          overscrollBehaviorX: 'contain',
          scrollbarGutter: 'stable both-edges',
          scrollbarWidth: 'auto',
          scrollbarColor: `${thumbBase} ${alpha(theme.palette.text.secondary, 0.14)}`,
          '&::-webkit-scrollbar': { width: 14, height: 14 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: thumbBase,
            borderRadius: 8,
            border: '3px solid transparent',
            backgroundClip: 'padding-box',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: alpha(theme.palette.text.secondary, 0.12),
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-corner': { background: 'transparent' },
        },
      }
    : {};

  const grid = (
    <DataGrid
      sx={{ border: 'none', ...appletSx, ...sx }}
      slots={mergedSlots}
      columns={orderedColumns}
      {...props}
    />
  );

  if (!listApplet) {
    return grid;
  }

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.14 : 0.09),
        borderRadius: 2,
        p: { xs: 0.75, sm: 1 },
        border: '1px solid',
        borderColor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.35 : 0.22),
        boxShadow: (t) =>
          t.palette.mode === 'dark'
            ? `inset 0 0 0 1px ${alpha(t.palette.common.white, 0.06)}`
            : `inset 3px 0 0 ${alpha(t.palette.primary.main, 0.55)}`,
        '&:hover .MuiDataGrid-virtualScroller': {
          scrollbarColor: `${thumbHover} ${alpha(theme.palette.text.secondary, 0.18)}`,
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: thumbHover,
          },
        },
      }}
    >
      {grid}
    </Box>
  );
}
